import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { billingApi } from '../api/billing';
import { DemoButton } from '../components/landing/DemoButton';
import { HeroMock } from '../components/landing/HeroMock';
import { LandingShell, LandingTop } from '../components/landing/LandingShell';
import { LatestPosts } from '../components/landing/LatestPosts';
import { SignUpLink } from '../components/landing/SignUpLink';
import {
  IconBell,
  IconCalculator,
  IconCheck,
  IconClock,
  IconCoins,
  IconDocument,
  IconGlobe,
  IconLock,
  IconPercent,
  IconShield,
  IconStack,
} from '../components/landing/icons';
import { eurToBgn, formatAmount, formatMoney } from '../lib/format';

// The /en prefix exists for search, not for the app: an English-speaking freelancer searching in
// English needs a page that ranks. Which language a marketing URL stands for is now decided by the
// language detector before the first render, and LandingShell keeps it in step on client-side
// navigation — this file used to carry its own copy of that effect, which was a second place for
// the same rule to drift out of agreement with the first.

const FEATURES = [
  { key: 'calculate', Icon: IconCalculator },
  { key: 'declarations', Icon: IconDocument },
  { key: 'deadlines', Icon: IconBell },
  { key: 'currencies', Icon: IconCoins },
  { key: 'bilingual', Icon: IconGlobe },
  { key: 'privacy', Icon: IconShield },
] as const;

const STEPS = ['profile', 'income', 'file'] as const;
/**
 * `wrong` and `advice` are here because they are the two questions a reader actually has and the two
 * a claim would later turn on. Answering them plainly on the marketing page — rather than only in
 * clause 7 of the terms — is both the honest thing and the more defensible one: a limitation of
 * liability that first appears after payment reads very differently from one the reader met before
 * they signed up.
 */
const FAQ = ['who', 'accurate', 'advice', 'wrong', 'filing', 'egn', 'price', 'refund'] as const;

/**
 * The work a самоосигуряващо се лице does across a year, and what the product does with each. The
 * feature grid below says what Danak has; this says what it removes, which is the question people
 * actually arrive with.
 *
 * Every "by hand" line is a real statutory requirement and every "with Danak" line is a feature
 * that exists today — no figures are quoted here beyond ones the law fixes (25%, 10%, the 25th) and
 * the obligation count, which is the same 33 the strip above counts out. Nothing on this page may
 * be aspirational: it is read by people deciding whether to trust the arithmetic.
 */
const WORK = [
  { key: 'monthly', Icon: IconCoins },
  { key: 'quarterly', Icon: IconCalculator },
  { key: 'annual', Icon: IconStack },
  { key: 'vat', Icon: IconPercent },
  { key: 'deadlines', Icon: IconBell },
  { key: 'invoices', Icon: IconDocument },
  { key: 'behind', Icon: IconClock },
] as const;

// Open to everybody, signed in or not. It used to send a signed-in visitor straight to the
// dashboard, and since the home page is where the blog, the pricing and the about page are linked
// from, that locked every customer out of the public site. The navigation already knows who is
// signed in and offers them the way back into the app instead of a sign-up.
export function LandingPage() {
  const { t } = useTranslation();

  return (
    <LandingShell>
      <LandingTop>
        <section className="l-hero">
          <div>
            <span className="l-pill">
              <IconLock size={14} />
              {t('marketing.heroPill')}
            </span>

            {/* Two clauses, the second in accent italic, so the headline has a turn in it rather
                than being one solid block. */}
            <h1>
              <Trans i18nKey="marketing.heroTitle" components={{ em: <em /> }} />
            </h1>

            <p className="l-hero-sub">{t('home.heroSubtitle')}</p>

            <div className="l-hero-actions">
              <SignUpLink className="l-btn l-btn--lg">{t('marketing.startTrial')}</SignUpLink>
              {/* Straight into a filled account — no email, no password, nothing to abandon.
                  For traffic arriving from an ad, the gap between the click and seeing a real
                  figure is where the conversion is won or lost, and a signup form sits in it. */}
              <DemoButton className="l-btn l-btn--lg l-btn--onDark" />
            </div>

            <div className="l-ticks">
              <span>
                <IconCheck size={15} />
                {t('marketing.ticks.free')}
              </span>
              <span>
                <IconCheck size={15} />
                {t('marketing.ticks.noCard')}
              </span>
              <span>
                <IconCheck size={15} />
                {t('marketing.ticks.cancel')}
              </span>
            </div>
          </div>

          <HeroMock />
        </section>
      </LandingTop>

      <div className="l-stats">
        <div className="l-stats-inner">
          <div>
            <span className="l-stat-figure">33</span>
            <p className="l-stat-text">{t('marketing.stats.obligations')}</p>
          </div>
          <div>
            <span className="l-stat-figure">7,5%</span>
            <p className="l-stat-text">{t('marketing.stats.effective')}</p>
          </div>
          <div>
            <span className="l-stat-figure">0</span>
            <p className="l-stat-text">{t('marketing.stats.noBank')}</p>
          </div>
        </div>
      </div>

      {/* Directly after the counted obligations, because this is the section that answers them:
          the strip says there are 33 of these a year, and this says what each one costs you and
          what is left once Danak has it. */}
      <section className="l-section" id="value">
        <div className="l-ledger-intro">
          <p className="l-eyebrow">{t('marketing.workEyebrow')}</p>
          <h2>{t('marketing.workTitle')}</h2>
          <p className="l-lead">{t('marketing.workLead')}</p>
        </div>

        <div className="l-ledger">
          {/* Column headings on wide screens only. Once the rows stack there is nothing left for
              them to head, so each cell carries its own label instead. */}
          <div className="l-ledger-head" aria-hidden="true">
            <span />
            <span>{t('marketing.work.manualHead')}</span>
            <span className="l-ledger-mine">{t('marketing.work.oursHead')}</span>
          </div>

          {WORK.map(({ key, Icon }) => (
            <article className="l-ledger-row" key={key}>
              <h3 className="l-ledger-job">
                <span className="l-ledger-icon">
                  <Icon size={17} />
                </span>
                {t(`marketing.work.${key}.job`)}
              </h3>

              <div className="l-ledger-manual">
                <span className="l-ledger-tag">{t('marketing.work.manualHead')}</span>
                <p>{t(`marketing.work.${key}.manual`)}</p>
              </div>

              <div className="l-ledger-ours">
                <span className="l-ledger-tag">{t('marketing.work.oursHead')}</span>
                <p>
                  <IconCheck size={15} />
                  {t(`marketing.work.${key}.ours`)}
                </p>
              </div>
            </article>
          ))}
        </div>

        <p className="l-ledger-foot">{t('marketing.workFoot')}</p>
      </section>

      <section className="l-section l-section--center" id="features">
        <p className="l-eyebrow">{t('marketing.featuresEyebrow')}</p>
        <h2>{t('marketing.featuresTitle')}</h2>
        <p className="l-lead">{t('marketing.featuresLead')}</p>

        <div className="l-grid">
          {FEATURES.map(({ key, Icon }) => (
            <article className="l-feature" key={key}>
              <div className="l-feature-icon">
                <Icon />
              </div>
              <p className="l-feature-tag">{t(`marketing.features.${key}.tag`)}</p>
              <h3>{t(`marketing.features.${key}.title`)}</h3>
              <p>{t(`marketing.features.${key}.body`)}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="l-section l-section--center" id="how">
        <p className="l-eyebrow">{t('marketing.howEyebrow')}</p>
        <h2>{t('marketing.howTitle')}</h2>
        <p className="l-lead">{t('marketing.howLead')}</p>

        <div className="l-steps">
          {STEPS.map((step, index) => (
            <article
              className={index === 1 ? 'l-step l-step--featured' : 'l-step'}
              key={step}
            >
              <span className="l-step-num">{index + 1}</span>
              <h3>{t(`marketing.steps.${step}.title`)}</h3>
              <p>{t(`marketing.steps.${step}.body`)}</p>
            </article>
          ))}
        </div>
      </section>

      <div className="l-band">
        <div className="l-band-inner">
          <div>
            <p className="l-eyebrow">{t('marketing.expatsEyebrow')}</p>
            <h2>{t('marketing.expatsTitle')}</h2>
            <p>{t('marketing.expatsBody')}</p>
          </div>

          <ul className="l-band-list">
            <li>
              <IconGlobe size={18} />
              {t('marketing.expats.ui')}
            </li>
            <li>
              <IconDocument size={18} />
              {t('marketing.expats.labels')}
            </li>
            <li>
              <IconCoins size={18} />
              {t('marketing.expats.currencies')}
            </li>
            <li>
              <IconBell size={18} />
              {t('marketing.expats.reminders')}
            </li>
          </ul>
        </div>
      </div>

      <section className="l-section l-section--center">
        <p className="l-eyebrow">{t('marketing.showcaseEyebrow')}</p>
        <h2>{t('marketing.showcaseTitle')}</h2>
        <p className="l-lead">{t('marketing.showcaseLead')}</p>

        <div className="l-showcase">
          <div>
            <div className="l-show-card">
              <span className="l-mock-pill">{t('marketing.mock.contributions')}</span>
              <p className="l-show-total">153,08 €</p>
              <p className="l-show-note">299,40 лв. · {t('marketing.show.perMonth')}</p>
              <ul className="l-show-rows">
                <li>
                  <span>{t('dashboard.funds.pension')}</span>
                  <span>81,50 €</span>
                </li>
                <li>
                  <span>{t('dashboard.funds.upf')}</span>
                  <span>27,53 €</span>
                </li>
                <li>
                  <span>{t('dashboard.funds.health')}</span>
                  <span>44,05 €</span>
                </li>
              </ul>
            </div>
            <h3>{t('marketing.show.fundsTitle')}</h3>
            <p>{t('marketing.show.fundsBody')}</p>
          </div>

          <div>
            <div className="l-show-card">
              <span className="l-mock-pill">{t('marketing.show.yearLabel')}</span>
              <p className="l-show-total">28 693,72 €</p>
              <p className="l-show-note">{t('marketing.show.taxableBase')}</p>
              <div className="l-show-bar">
                <i style={{ width: '62%' }} />
              </div>
              <ul className="l-show-rows">
                <li>
                  <span>{t('dashboard.annualTax')}</span>
                  <span>2 869,37 €</span>
                </li>
                <li>
                  <span>{t('dashboard.balanceDue')}</span>
                  <span className="l-show-good">311,00 €</span>
                </li>
              </ul>
            </div>
            <h3>{t('marketing.show.annualTitle')}</h3>
            <p>{t('marketing.show.annualBody')}</p>
          </div>

          <div>
            <div className="l-show-card">
              <span className="l-mock-pill">{t('marketing.mock.upcoming')}</span>
              <ul className="l-show-rows">
                <li>
                  <span className="l-mock-icon">Д1</span>
                  <span>25.02.2026</span>
                  <span>—</span>
                </li>
                <li>
                  <span className="l-mock-icon">55</span>
                  <span>30.04.2026</span>
                  <span>854,08 €</span>
                </li>
                <li>
                  <span className="l-mock-icon">50</span>
                  <span>04.05.2027</span>
                  <span>311,00 €</span>
                </li>
              </ul>
              <p className="l-show-note">{t('marketing.show.shifted')}</p>
            </div>
            <h3>{t('marketing.show.deadlinesTitle')}</h3>
            <p>{t('marketing.show.deadlinesBody')}</p>
          </div>
        </div>
      </section>

      <LatestPosts />

      <section className="l-section l-section--center">
        <p className="l-eyebrow">{t('marketing.faqEyebrow')}</p>
        <h2>{t('marketing.faqTitle')}</h2>

        <div className="l-faq">
          {FAQ.map((item) => (
            <details key={item}>
              <summary>{t(`marketing.faq.${item}.q`)}</summary>
              <p>{t(`marketing.faq.${item}.a`)}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="l-cta">
        <h2>{t('marketing.ctaTitle')}</h2>
        <p>{t('marketing.ctaBody')}</p>
        <SignUpLink className="l-btn l-btn--lg l-btn--onGradient">{t('marketing.startTrial')}</SignUpLink>
        <p className="l-cta-note">{t('marketing.trialNote')}</p>
      </section>
    </LandingShell>
  );
}

export function PricingPage() {
  const { t, i18n } = useTranslation();

  const english = i18n.resolvedLanguage === 'en';
  const locale = english ? 'en-GB' : 'bg-BG';
  const { data: plans } = useQuery({ queryKey: ['billing-plans'], queryFn: billingApi.plans });

  const proFeatures = plans?.[0] ? (english ? plans[0].featuresEn : plans[0].featuresBg) : [];
  const monthly = plans?.find((p) => p.period === 'Monthly')?.price ?? null;
  const annual = plans?.find((p) => p.period === 'Annual')?.price ?? null;

  return (
    <LandingShell>
      <LandingTop>
        <section className="l-hero" style={{ gridTemplateColumns: '1fr', textAlign: 'center' }}>
          <div>
            <p className="l-eyebrow" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {t('marketing.pricingEyebrow')}
            </p>
            <h1>{t('marketing.pricingTitle')}</h1>
            <p className="l-hero-sub" style={{ marginInline: 'auto' }}>
              {t('marketing.pricingLead')}
            </p>
          </div>
        </section>
      </LandingTop>

      <section className="l-section l-section--center">
        <div className="l-plans">
          <article className="l-plan">
            <h3>{t('billing.free')}</h3>
            <p className="l-plan-price">
              0 € <span>{t('marketing.perMonth')}</span>
            </p>
            <p className="l-plan-note">{t('marketing.freeNote')}</p>
            <ul>
              <li>{t('marketing.freeFeatures.calculator')}</li>
              <li>{t('marketing.freeFeatures.calendar')}</li>
              <li>{t('marketing.freeFeatures.income')}</li>
            </ul>
            <SignUpLink className="l-btn l-btn--ghost">{t('home.cta')}</SignUpLink>
          </article>

          <article className="l-plan l-plan--featured">
            <span className="l-plan-badge">{t('marketing.recommended')}</span>
            <h3>{t('billing.pro')}</h3>
            {/* The amount is Stripe's, read through /api/billing/plans, so the page can only ever
                show what Checkout charges. Until Stripe is configured there is no number to show —
                inventing one here is the one thing that must not happen — and the trial is what
                there is to say. */}
            {monthly ? (
              <>
                <p className="l-plan-price">
                  {formatAmount(monthly.amount, monthly.currency, locale)}{' '}
                  <span>{t('marketing.perMonth')}</span>
                </p>
                <p className="l-plan-note">
                  {[
                    monthly.currency === 'EUR' ? formatMoney(eurToBgn(monthly.amount), 'BGN', locale) : null,
                    annual
                      ? t('marketing.orAnnual', { amount: formatAmount(annual.amount, annual.currency, locale) })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </>
            ) : (
              <>
                <p className="l-plan-price">
                  30 <span>{t('marketing.trialDays')}</span>
                </p>
                <p className="l-plan-note">{t('marketing.priceSoon')}</p>
              </>
            )}
            <ul>
              {proFeatures.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            {/* With an account, the useful door from a price is the page where the plan is changed. */}
            <SignUpLink className="l-btn" accountTo="/billing" accountLabel={t('nav.billing')}>
              {t('marketing.startTrial')}
            </SignUpLink>
          </article>
        </div>

        <p className="l-lead" style={{ marginTop: '1.5rem' }}>
          {t('marketing.trialNote')}
        </p>
      </section>

      <section className="l-section l-section--center">
        <p className="l-eyebrow">{t('marketing.faqEyebrow')}</p>
        <h2>{t('marketing.faqTitle')}</h2>
        <div className="l-faq">
          {(['price', 'filing', 'accurate'] as const).map((item) => (
            <details key={item}>
              <summary>{t(`marketing.faq.${item}.q`)}</summary>
              <p>{t(`marketing.faq.${item}.a`)}</p>
            </details>
          ))}
        </div>
      </section>
    </LandingShell>
  );
}

/**
 * The sections of the terms, in the order someone reads them: who we are, what the thing does, whose
 * job is whose, then money, then the clauses you only look up when something has gone wrong.
 *
 * `provider` is first because чл. 4 ЗЕТ requires the provider's identity to be directly and
 * permanently accessible, and it is the one section that is currently unfinished — the company
 * details are facts nobody has supplied. It renders its placeholders visibly rather than quietly, so
 * that going live without them is impossible to miss.
 */
const TERMS_SECTIONS = [
  'provider',
  'what',
  'yours',
  'account',
  'withdrawal',
  'termination',
  'liability',
  'changes',
  'law',
] as const;

export function TermsPage() {
  const { t } = useTranslation();

  return (
    <LandingShell>
      <LandingTop>
        <section className="l-hero" style={{ gridTemplateColumns: '1fr', paddingBottom: '3rem' }}>
          <div>
            <p className="l-eyebrow" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {t('marketing.legalEyebrow')}
            </p>
            <h1>{t('terms.title')}</h1>
            <p className="l-hero-sub">{t('terms.updated')}</p>
          </div>
        </section>
      </LandingTop>

      <section className="l-section">
        <div className="l-prose">
          {/* Said before the terms rather than inside them: it is the one thing a reader needs to
              know about this product and it should not be a clause they have to reach. */}
          <p className="l-terms-lead">{t('terms.lead')}</p>

          {TERMS_SECTIONS.map((key) => (
            <div key={key}>
              <h2>{t(`terms.${key}.title`)}</h2>
              <p>{t(`terms.${key}.body`)}</p>
            </div>
          ))}

          <p className="l-plan-note" style={{ marginTop: '2rem' }}>
            {t('terms.reviewNote')}
          </p>
        </div>
      </section>
    </LandingShell>
  );
}

export function PrivacyPage() {
  const { t } = useTranslation();

  return (
    <LandingShell>
      <LandingTop>
        <section className="l-hero" style={{ gridTemplateColumns: '1fr', paddingBottom: '3rem' }}>
          <div>
            <p className="l-eyebrow" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {t('marketing.legalEyebrow')}
            </p>
            <h1>{t('privacy.title')}</h1>
          </div>
        </section>
      </LandingTop>

      <section className="l-section">
        <div className="l-prose">
          {(['whatWeStore', 'egn', 'retention', 'rights', 'tracking', 'processors'] as const).map((key) => (
            <div key={key}>
              <h2>{t(`privacy.${key}.title`)}</h2>
              <p>{t(`privacy.${key}.body`)}</p>
            </div>
          ))}

          <p className="l-plan-note" style={{ marginTop: '2rem' }}>
            {t('privacy.placeholderNote')}
          </p>
        </div>
      </section>
    </LandingShell>
  );
}
